import { useCallback, useMemo, useState } from "react";
import { min, differenceInCalendarDays } from "date-fns";
import moment from "moment-timezone";
import { DateRange } from "react-day-picker";
import { toast } from "sonner";
import { FilterSelect } from "src/@/components/ui/filter-select";
import { DateRangePicker } from "src/@/components/ui/date-range-picker";
import { Button } from "src/@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { DefinedDates, getDefinedDatesTranslations, useOrders } from "../orders-context";
import { Routes } from "#navigation/routes";
import { DatePicker } from "src/@/components/ui/date-picker";
import { MAX_DATE_RANGE } from "#orders/use-query-mongo-orders";
import { useSelector } from "react-redux";
import {
    getActiveStore,
    getUser,
    getUserIsSnackpassEmployee,
} from "src/redux/selectors";


export const OrdersDateSelector = () => {
    const { duration, setDuration, setTimeRange, startDate, endDate } =
        useOrders();

    // localDuration tracks SPECIFIC_DAY / DATE_RANGE before the user clicks
    // Apply. Null means the URL-backed duration is the source of truth.
    const [localDuration, setLocalDuration] = useState<DefinedDates | null>(null);
    const activeDuration = localDuration ?? duration;

    const [isOpen, setIsOpen] = useState(false);
    const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>({
        from: startDate.toDate(),
        to: min([endDate.toDate(), new Date()]),
    });
    const [hasChanges, setHasChanges] = useState(false);
    const user = useSelector(getUser);
    const activeStore = useSelector(getActiveStore);
    const isSnackpassEmployee = useSelector(getUserIsSnackpassEmployee);
    const KINGS_KITCHEN_HARD_CODE_BLOCK_DATE = useMemo(() => {
        const KINGS_KITCHEN_ID = "66f6e600a62affdda0fdadd0";
        return (
            activeStore?._id == KINGS_KITCHEN_ID &&
            !isSnackpassEmployee &&
            user?._id !== "66f6e604a62affdda0fdb55f" &&
            user?._id !== "61300e444e254101266f5928"
        );
    }, [activeStore?._id, user?._id, isSnackpassEmployee]);

    const formatDate = (date: moment.Moment) => {
        if (!date) return "";
        return date.format("MMM D, YYYY");
    };

    const dateRangeText =
        startDate && endDate
            ? startDate.isSame(endDate, "day")
                ? formatDate(endDate)
                : `${formatDate(startDate)} - ${formatDate(endDate)}`
            : "Select dates";

    const handleCustomDateChange = useCallback(
        (value: DateRange | undefined) => {
            setTempDateRange(value);
            setHasChanges(true);
        },
        [],
    );

    const handleApplyDateRange = useCallback(() => {
        if (!tempDateRange?.from || !tempDateRange?.to) {
            setTimeRange({
                startDate: moment().startOf("day"),
                endDate: moment().endOf("day"),
            });
            return;
        }

        const numberOfDays =
            differenceInCalendarDays(
                tempDateRange.to,
                tempDateRange.from,
            ) + 1;
        if (numberOfDays > MAX_DATE_RANGE) {
            toast.error("Range too long", {
                description: `Please use Sales Report for ranges longer than ${MAX_DATE_RANGE} days`,
                action: {
                    label: "Go To Sales Report",
                    onClick: () =>
                        (window.location.href =
                            Routes.FinancialReportsEssentials),
                },
            });
            return;
        }

        setTimeRange({
            startDate: moment(tempDateRange.from).startOf("day"),
            endDate: moment(tempDateRange.to).endOf("day"),
        });
        setLocalDuration(null);
        setHasChanges(false);
    }, [tempDateRange, setTimeRange]);

    const handleDurationSelection = useCallback(
        (value: string) => {
            const newDuration = value as DefinedDates;

            // SPECIFIC_DAY and DATE_RANGE don't update the URL yet —
            // they just show the date picker UI. The URL updates when
            // the user clicks "Apply" via setTimeRange.
            if (newDuration === DefinedDates.SPECIFIC_DAY) {
                setTempDateRange({
                    from: startDate.toDate(),
                    to: startDate.toDate(),
                });
                setHasChanges(false);
                setLocalDuration(newDuration);
                setIsOpen(false);
                return;
            }
            if (newDuration === DefinedDates.DATE_RANGE) {
                setTempDateRange({
                    from: startDate.toDate(),
                    to: min([endDate.toDate(), new Date()]),
                });
                setHasChanges(false);
                setLocalDuration(newDuration);
                return;
            }

            // For presets, setDuration updates the URL immediately —
            // dates are derived from the duration in useOrdersSearchParams.
            setLocalDuration(null);
            setDuration(newDuration);

            const now = moment();
            let start: moment.Moment;
            let end: moment.Moment;

            switch (newDuration) {
                case DefinedDates.LAST7DAYS: {
                    end = now.clone().subtract(1, "days").endOf("day");
                    start = end.clone().subtract(6, "days").startOf("day");
                    break;
                }
                case DefinedDates.TODAY:
                    start = now.clone().startOf("day");
                    end = now.clone().endOf("day");
                    break;
                case DefinedDates.YESTERDAY:
                    start = now.clone().subtract(1, "day").startOf("day");
                    end = now.clone().subtract(1, "day").endOf("day");
                    break;
                default:
                    return;
            }

            setTempDateRange({ from: start.toDate(), to: end.toDate() });
        },
        [endDate, setDuration, startDate],
    );

    const handleSpecificDayChange = useCallback((date: Date | undefined) => {
        if (!date) return;
        setTempDateRange({
            from: date,
            to: date,
        });
        setHasChanges(true);
    }, []);

    const ALLOWED_DURATIONS = [
        DefinedDates.TODAY,
        DefinedDates.YESTERDAY,
        DefinedDates.LAST7DAYS,
        DefinedDates.SPECIFIC_DAY,
        DefinedDates.DATE_RANGE,
    ];

    const definedDatesTranslations = getDefinedDatesTranslations();
    const options = ALLOWED_DURATIONS.map((e) => ({
        label: definedDatesTranslations[e],
        value: e,
    }));

    return (
        <div className="flex items-center gap-2">
            <FilterSelect
                customButton={
                    <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center"
                        disabled={KINGS_KITCHEN_HARD_CODE_BLOCK_DATE}
                    >
                        <CalendarIcon className="h-4 w-4" />
                        <span className="hidden md:block">
                            {definedDatesTranslations[activeDuration]}
                        </span>
                    </Button>
                }
                title={definedDatesTranslations[activeDuration]}
                subtitle={dateRangeText}
                selectedValues={new Set([activeDuration])}
                options={options}
                onOptionSelected={handleDurationSelection}
                open={isOpen}
                onOpenChange={setIsOpen}
            />
            {activeDuration === DefinedDates.SPECIFIC_DAY && (
                <div className="flex items-center gap-2">
                    <DatePicker
                        date={tempDateRange?.from}
                        onDateChange={handleSpecificDayChange}
                        disabled={(date) => date > new Date()}
                    />
                    <Button
                        size="sm"
                        onClick={handleApplyDateRange}
                        disabled={!tempDateRange?.from || !hasChanges}
                    >
                        Apply
                    </Button>
                </div>
            )}
            {activeDuration === DefinedDates.DATE_RANGE && (
                <div className="flex items-center gap-2">
                    <DateRangePicker
                        date={tempDateRange}
                        onDateChange={handleCustomDateChange}
                        disableFutureDates
                    />
                    <Button
                        size="sm"
                        onClick={handleApplyDateRange}
                        disabled={
                            !tempDateRange?.from ||
                            !tempDateRange?.to ||
                            !hasChanges
                        }
                    >
                        Apply
                    </Button>
                </div>
            )}
        </div>
    );
};
